import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { uniqueCategories } from './categories';
import RateCardRoleModal from './RateCardRoleModal';
import RateCardRolesTable from './RateCardRolesTable';
import RateCardVersionHistoryModal from './RateCardVersionHistoryModal';
import RateCardVersionModal from './RateCardVersionModal';

import { formatDateLong } from 'Helpers/dateFormat';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import SelectInput from 'CommonComponents/SelectInput';
import TextButton from 'CommonComponents/TextButton';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

function versionLabel(version) {
    const period = `${formatDateLong(version.starts_on)} – ${formatDateLong(version.ends_on)}`;

    return version.is_current ? `${version.name} · ${period} · ${$t('Current')}` : `${version.name} · ${period}`;
}

export default function OrganisationRateCardIndex(props) {
    const [versions, setVersions] = useState([]);
    const [currency, setCurrency] = useState(null);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [roleModal, setRoleModal] = useState(null);
    const [versionModal, setVersionModal] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const toast = useToast();

    // Archiving is the closest thing here to a destructive action — it drops a
    // role out of every future version's clone — so it asks first. Restore does
    // not: it only puts back what archiving took away.
    const confirmArchive = useConfirmationModal({
        message: $t('Are you sure you want to archive this role?'),
        warning: $t('It stops appearing on new versions cloned from this one. Existing versions keep it, and you can restore it at any time.'),
    });

    const confirmDeleteVersion = useConfirmationModal({
        message: $t('Are you sure you want to delete this version?'),
        warning: $t('It has no roles on it, so no rates are lost. This cannot be undone.'),
    });

    const fetchData = useCallback(
        (preferVersionId = null) => {
            setLoading(true);

            return axios
                .get(props.dataUrl)
                .then(({ data }) => {
                    const loaded = data.data ?? [];
                    setVersions(loaded);
                    setCurrency(data.meta?.currency ?? null);

                    setSelectedVersionId((current) => {
                        const wanted = preferVersionId ?? current ?? data.meta?.current_version_id ?? null;

                        return loaded.some((version) => version.id === wanted) ? wanted : (loaded[0]?.id ?? null);
                    });
                })
                .catch(() => {
                    setError($t('Something went wrong'));
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [props.dataUrl],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const selectedVersion = versions.find((version) => version.id === selectedVersionId) ?? null;
    const roles = selectedVersion?.roles ?? [];
    const categories = uniqueCategories(roles);

    const roleUpdateUrl = (roleId) => props.roleUpdateUrl.replace('__ROLE__', roleId);
    const versionUpdateUrl = (versionId) => props.versionUpdateUrl.replace('__VERSION__', versionId);
    const versionDeleteUrl = (versionId) => props.versionDeleteUrl.replace('__VERSION__', versionId);
    const rolesStoreUrl = selectedVersionId ? props.rolesStoreUrl.replace('__VERSION__', selectedVersionId) : null;

    // Archive and restore are the same write — the status a role is saved with.
    // Nothing in the feature deletes.
    const saveRoleStatus = (role, status) => {
        axios
            .put(roleUpdateUrl(role.id), {
                name: role.name,
                category: role.category,
                unit: role.unit,
                cost_rate: role.cost_rate,
                billed_rate: role.billed_rate,
                default_type: role.default_type,
                status,
            })
            .then(() => {
                toast.success(status === 'archived' ? $t('The role was archived') : $t('The role was restored'));
                fetchData();
            })
            .catch(() => {
                toast.error($t('Something went wrong'));
            });
    };

    const archiveRole = (role) => {
        confirmArchive({
            title: $t('Archiving role - {{role_name}}', { role_name: role.name }),
            onConfirm: () => saveRoleStatus(role, 'archived'),
        });
    };

    // Offered only while the version holds no roles — a version that has ever
    // carried rates is kept, and the server refuses it either way.
    const deleteVersion = (version) => {
        confirmDeleteVersion({
            title: $t('Deleting version - {{version_name}}', { version_name: version.name }),
            onConfirm: () => {
                axios
                    .delete(versionDeleteUrl(version.id))
                    .then(() => {
                        toast.success($t('The version was deleted'));
                        setSelectedVersionId(null);
                        fetchData();
                    })
                    .catch((e) => {
                        // The one refusal this call has is a designed, translated
                        // sentence naming why the version is kept. Falling back to
                        // the generic string would throw it away.
                        toast.error(e.response?.data?.message || $t('Something went wrong'));
                    });
            },
        });
    };

    return (
        <div className="tw-space-y-4">
            {error && <Alert type="error">{error}</Alert>}

            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                <div>
                    <h1 className="table__header tw-mb-1">{$t('Rate card')}</h1>
                    <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                        {$t('Organisation-level roles and rates, referenced by time entries in every project')}
                        {' · '}
                        <span className="tw-text-blue">{$t('Organisation Admin only')}</span>
                    </p>
                </div>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {versions.length > 0 && (
                        <SelectInput
                            name="rate_card_version"
                            aria-label={$t('Version')}
                            value={selectedVersionId ?? ''}
                            onChange={(event) => setSelectedVersionId(Number(event.target.value))}
                        >
                            {versions.map((version) => (
                                <option key={version.id} value={version.id}>
                                    {versionLabel(version)}
                                </option>
                            ))}
                        </SelectInput>
                    )}

                    <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
                        {$t('Version history')}
                    </Button>

                    <Button variant="secondary" onClick={() => setVersionModal({ version: null, cloneFrom: selectedVersion })}>
                        {$t('New version')}
                    </Button>

                    <Button variant="primary" disabled={!selectedVersionId} onClick={() => setRoleModal({ role: null })}>
                        {$t('Add role')}
                    </Button>
                </div>
            </div>

            <Alert type="info" role="status">
                <strong>{$t('Visibility')}</strong>
                {' — '}
                {$t(
                    'this page and its menu item render for Organisation Admins only. Cost rates and margin never leave this role; everyone else only ever sees a role name and a billed rate when logging time.',
                )}
            </Alert>

            <div className="table__card">
                {selectedVersion && (
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-6">
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-0">{selectedVersion.name}</h2>
                            <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                                {formatDateLong(selectedVersion.starts_on)} – {formatDateLong(selectedVersion.ends_on)}
                            </p>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-x-4">
                            <TextButton variant="primary" onClick={() => setVersionModal({ version: selectedVersion, cloneFrom: null })}>
                                {$t('Edit version dates')}
                            </TextButton>
                            {/* Roles are archived, never deleted. An empty version is a
                                different thing: nothing to lose, and the only way back
                                from one created with the wrong dates. */}
                            {roles.length === 0 && (
                                <TextButton variant="danger" onClick={() => deleteVersion(selectedVersion)}>
                                    <TrashIcon className="tw-h-3 tw-w-3" />
                                    {$t('Delete version')}
                                </TextButton>
                            )}
                        </div>
                    </div>
                )}

                {loading && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>}

                {!loading && versions.length === 0 && (
                    <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                        {$t('No rate card versions yet. Create one to set the roles and rates time is charged at.')}
                    </p>
                )}

                {!loading && versions.length > 0 && (
                    <RateCardRolesTable
                        roles={roles}
                        currency={currency}
                        onEdit={(role) => setRoleModal({ role })}
                        onArchive={archiveRole}
                        onRestore={(role) => saveRoleStatus(role, 'active')}
                    />
                )}
            </div>

            {roleModal && rolesStoreUrl && (
                <RateCardRoleModal
                    role={roleModal.role}
                    categories={categories}
                    storeUrl={rolesStoreUrl}
                    updateUrl={roleUpdateUrl}
                    onSaved={() => fetchData()}
                    onClose={() => setRoleModal(null)}
                />
            )}

            {versionModal && (
                <RateCardVersionModal
                    version={versionModal.version}
                    cloneFromVersion={versionModal.cloneFrom}
                    storeUrl={props.versionsStoreUrl}
                    updateUrl={versionUpdateUrl}
                    onSaved={(saved) => fetchData(saved?.id ?? null)}
                    onClose={() => setVersionModal(null)}
                />
            )}

            {historyOpen && (
                <RateCardVersionHistoryModal
                    versions={versions}
                    selectedVersionId={selectedVersionId}
                    onSelect={(id) => setSelectedVersionId(id)}
                    onClose={() => setHistoryOpen(false)}
                />
            )}
        </div>
    );
}

if (document.getElementById('OrganisationRateCard')) {
    const rootEl = document.getElementById('OrganisationRateCard');

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <OrganisationRateCardIndex
                    dataUrl={rootEl.getAttribute('data-data-url')}
                    versionsStoreUrl={rootEl.getAttribute('data-versions-store-url')}
                    versionUpdateUrl={rootEl.getAttribute('data-version-update-url')}
                    versionDeleteUrl={rootEl.getAttribute('data-version-delete-url')}
                    rolesStoreUrl={rootEl.getAttribute('data-roles-store-url')}
                    roleUpdateUrl={rootEl.getAttribute('data-role-update-url')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
